import { useSuspenseQuery } from '@apollo/client/react';
import { cloneDeep } from 'lodash';
import { useEffect } from 'react';

import { EscrowStatusEnum, SuggestedChangeStatusEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { SuggestedChangesNotificationsDocument } from 'src/graphql/actions/suggestChanges/suggestedChangesNotifications.query';
import { suggestChangesVar, writeInitialSuggestChangesData } from 'src/graphql/client/cache';
import { useSaasEscrow } from 'src/context/saas-escrow';

import type { ISuggestedChangesNotificationsQuery } from 'src/graphql/actions/suggestChanges/suggestedChangesNotifications.query';

/* eslint-disable react/display-name */
export const withSuggestedChangesApi = (Component: React.ElementType) => () => {
  const {
    saasEscrow: { id: escrowId, status },
  } = useSaasEscrow();
  const { data } = useSuspenseQuery(SuggestedChangesNotificationsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      escrowId,
    },
    skip:
      !escrowId ||
      [
        EscrowStatusEnum.Draft,
        EscrowStatusEnum.AdminReview,
        EscrowStatusEnum.PendingTermination,
        EscrowStatusEnum.Terminated,
      ].includes(status),
  });

  useEffect(() => {
    const onCompleted = ({
      agreementSuggestedChanges,
      depositorSuggestedChanges,
      beneficiarySuggestedChanges,
      notificationSuggestedChanges,
      depositSuggestedChanges,
    }: ISuggestedChangesNotificationsQuery) => {
      // Check if any suggestions are requested
      const isRequested =
        Boolean(depositorSuggestedChanges.nodesCount) ||
        Boolean(beneficiarySuggestedChanges.nodesCount) ||
        Boolean(depositSuggestedChanges.nodesCount) ||
        Boolean(notificationSuggestedChanges.nodesCount) ||
        Boolean(agreementSuggestedChanges.nodesCount);

      if (!isRequested) return;

      let allChanges = cloneDeep(suggestChangesVar()) as ReturnType<typeof suggestChangesVar>;

      // A helper to apply changes for a given key if there are nodes
      const applyChange = (
        key: keyof typeof allChanges,
        changesObj: {
          nodesCount: number;
          nodes: ISuggestedChangesNotificationsQuery['agreementSuggestedChanges']['nodes'];
        },
      ) => {
        if (changesObj.nodes.length) {
          const node = changesObj.nodes[0];

          if (node.partyStatus === SuggestedChangeStatusEnum.Rejected) return;

          allChanges = {
            ...allChanges,
            [key]: {
              id: node.id,
              status: node.partyStatus as SuggestedChangeStatusEnum,
              creator: node.creatorType,
              receiver: node.receiverType as EscrowPartyTypeEnum,
              // @ts-expect-error dynamic type
              payload: allChanges[key]?.payload ?? null,
            },
          };
        }
      };

      applyChange('depositor', depositorSuggestedChanges);
      applyChange('beneficiary', beneficiarySuggestedChanges);
      applyChange('deposits', depositSuggestedChanges);
      applyChange('notifications', notificationSuggestedChanges);
      applyChange('agreement', agreementSuggestedChanges);

      writeInitialSuggestChangesData(suggestChangesVar)(allChanges);
    };

    if (data) onCompleted(data);
  }, [data]);

  return <Component />;
};
